// ============================================
// MoltiVerse v3 - Main Application
// ============================================

import { useEffect, useState, memo, useRef } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { useUniverseStore } from '@/stores/universe-store';
import { GalaxyScene } from '@/components/Galaxy/Scene';
import { ControlDeck } from '@/components/HUD/ControlDeck';
import { SearchOmnibar } from '@/components/HUD/SearchOmnibar';
import { AgentDNAPanel } from '@/components/HUD/AgentDNAPanel';
import { NetworkFlexCard } from '@/components/HUD/NetworkFlexCard';
import { LoadingScreen } from '@/components/HUD/LoadingScreen';
import { StatsOverlay } from '@/components/HUD/StatsOverlay';
import { TimelineScrubber } from '@/components/HUD/TimelineScrubber';
import { ErrorBoundary, SceneErrorFallback } from '@/components/ErrorBoundary';

function AppContent() {
  const {
    initialize,
    loadUniverse,
    isLoading,
    loadProgress,
    stats,
    view,
    selectedAgent,
    search,
    openSearch,
    goBack,
    canGoBack,
  } = useUniverseStore();

  const [initialized, setInitialized] = useState(false);
  const [showFlexCard, setShowFlexCard] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevModeRef = useRef(view.mode);
  
  // Close flex card when agent changes or mode changes
  useEffect(() => {
    setShowFlexCard(false);
  }, [selectedAgent?.id, view.mode]);
  
  // Show transition overlay when exiting pathfinder mode
  useEffect(() => {
    const wasPathfinder = prevModeRef.current === 'pathfinder';
    prevModeRef.current = view.mode;
    
    if (wasPathfinder && view.mode === 'galaxy') {
      setIsTransitioning(true);
      // Hide overlay after camera animation completes
      setTimeout(() => setIsTransitioning(false), 600);
    }
  }, [view.mode]);

  useEffect(() => {
    const init = async () => {
      await initialize();
      setInitialized(true);
      
      // Get fresh stats after initialization to check if we have cached data
      const currentStats = useUniverseStore.getState().stats;
      if (currentStats.loadedAgents === 0) {
        loadUniverse();
      }
    };
    
    init();
  }, []);
  
  // Keyboard shortcut for back navigation (Backspace when not in search)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      
      // Backspace to go back
      if (e.key === 'Backspace' && canGoBack) {
        e.preventDefault();
        goBack();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canGoBack, goBack]);

  if (!initialized) {
    return <LoadingScreen progress={0} message="Initializing Oracle..." />;
  }

  return (
    <div className="w-full h-full bg-[#030306] relative overflow-hidden">
      {/* Ambient background effects */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Subtle grid */}
        <div className="absolute inset-0 grid-bg opacity-20" />
        
        {/* Vignette */}
        <div className="absolute inset-0 vignette" />
        
        {/* Ambient glow spots */}
        <div 
          className="absolute w-[600px] h-[600px] rounded-full blur-[150px] opacity-20"
          style={{ 
            top: '10%', 
            left: '20%', 
            background: 'radial-gradient(circle, rgba(0, 255, 136, 0.3) 0%, transparent 70%)' 
          }}
        />
        <div 
          className="absolute w-[500px] h-[500px] rounded-full blur-[150px] opacity-15"
          style={{ 
            bottom: '20%', 
            right: '15%', 
            background: 'radial-gradient(circle, rgba(128, 0, 255, 0.3) 0%, transparent 70%)' 
          }}
        />
      </div>

      {/* 3D Galaxy Scene - only render after loading to prevent freeze */}
      {!isLoading && (
        <ErrorBoundary fallback={<SceneErrorFallback />}>
          <GalaxyScene />
        </ErrorBoundary>
      )}
      
      {/* Loading overlay */}
      {isLoading && (
        <LoadingScreen 
          progress={loadProgress} 
          message={loadProgress < 100 ? `Syncing with Oracle... ${Math.round(loadProgress)}%` : 'Preparing visualization...'}
          overlay
        />
      )}
      
      {/* Mode transition overlay - smooth fade when exiting pathfinder */}
      {isTransitioning && (
        <div 
          className="fixed inset-0 z-40 bg-[#030306] pointer-events-none"
          style={{
            animation: 'fadeOut 0.6s ease-out forwards',
          }}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full border-2 border-cyber-glow/30 border-t-cyber-glow animate-spin" />
              <p className="font-mono text-sm text-white/50">Returning to Galaxy View...</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Top right - Mode indicator - hidden in pathfinder mode to avoid overlap */}
      {view.mode !== 'pathfinder' && (
        <div className={`fixed top-6 z-50 pointer-events-auto transition-all duration-300 ${
          selectedAgent ? 'right-[340px]' : 'right-6'
        }`}>
          <div className="flex items-center gap-3">
            {/* Mode badge */}
            <div className="relative group">
              <div className="absolute inset-0 bg-cyber-glow/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative flex items-center gap-3 px-4 py-2.5 bg-black/60 backdrop-blur-md border border-white/10 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] text-white/40 tracking-widest">MODE</span>
                  <div className="w-px h-4 bg-white/10" />
                </div>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${
                    view.mode === 'galaxy' ? 'bg-cyber-glow' : 'bg-purple-400'
                  }`} />
                  <span className="font-display text-sm font-bold text-white uppercase tracking-wider">
                    {view.mode.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Stats Overlay - Wrapped in error boundary for isolation */}
      <ErrorBoundary fallback={<div className="fixed top-6 left-6 z-40 px-4 py-2 bg-red-500/20 border border-red-500/40 rounded-lg font-mono text-xs text-red-400">Stats unavailable</div>}>
        <StatsOverlay />
      </ErrorBoundary>
      
      {/* Search Bar Trigger - Hidden in pathfinder mode to avoid overlap */}
      {view.mode !== 'pathfinder' && (
        <button
          onClick={openSearch}
          className="fixed top-3 sm:top-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-2.5 bg-black/70 border border-white/20 rounded-xl backdrop-blur-sm hover:border-cyber-glow/50 hover:bg-black/80 transition-all group cursor-pointer"
        >
          <svg className="w-4 h-4 text-white/40 group-hover:text-cyber-glow transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span className="text-white/50 group-hover:text-white/70 text-xs sm:text-sm font-mono transition-colors">Search...</span>
          <kbd className="hidden sm:block px-2 py-0.5 bg-white/10 rounded text-xs text-white/30 group-hover:text-white/50 transition-colors">⌘K</kbd>
        </button>
      )}
      
      {/* Control Deck - Disabled for now */}
      {/* {view.mode !== 'pathfinder' && <ControlDeck />} */}
      
      {/* Timeline Scrubber (Bottom, when in timelapse mode) */}
      {view.mode === 'timelapse' && <TimelineScrubber />}
      
      {/* Search Omnibar */}
      {search.isOpen && <SearchOmnibar />}
      
      {/* Agent DNA Panel (Right side on desktop, bottom sheet on mobile) - with error boundary */}
      {selectedAgent && (
        <ErrorBoundary fallback={
          <div className="fixed inset-x-0 bottom-0 md:inset-auto md:top-6 md:right-4 md:bottom-6 md:w-80 z-50 bg-red-500/10 border border-red-500/30 rounded-t-2xl md:rounded-lg p-4 text-center">
            <div className="text-2xl mb-2">⚠️</div>
            <div className="font-mono text-xs text-red-400">Agent panel error</div>
            <button 
              onClick={() => useUniverseStore.getState().focusAgent(null)}
              className="mt-3 px-4 py-2 bg-white/10 rounded text-xs text-white/60"
            >
              Close
            </button>
          </div>
        }>
          <AgentDNAPanel agent={selectedAgent} />
        </ErrorBoundary>
      )}
      
      {/* Flex Card Toggle Button (Bottom center, in pathfinder mode) */}
      {selectedAgent && view.mode === 'pathfinder' && !showFlexCard && (
        <button
          onClick={() => setShowFlexCard(true)}
          className="fixed bottom-4 sm:bottom-8 left-1/2 -translate-x-1/2 z-50 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-mono text-xs sm:text-sm font-bold transition-all hover:scale-105 active:scale-95"
          style={{
            background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(168, 85, 247, 0.2))',
            border: '2px solid rgba(251, 191, 36, 0.5)',
            color: '#fbbf24',
            boxShadow: '0 0 30px rgba(251, 191, 36, 0.3), 0 10px 40px rgba(0,0,0,0.5)',
          }}
        >
          📊 <span className="hidden sm:inline">View </span>Network Stats
        </button>
      )}
      
      {/* Network Flex Card (Modal overlay, when opened) */}
      {selectedAgent && view.mode === 'pathfinder' && showFlexCard && (
        <NetworkFlexCard 
          agent={selectedAgent} 
          onClose={() => setShowFlexCard(false)} 
        />
      )}
      
      {/* Corner frame decorations - hidden on mobile */}
      <div className="hidden sm:block fixed inset-4 pointer-events-none z-30">
        <div className="absolute top-0 left-0 w-16 h-16 border-l-2 border-t-2 border-cyber-glow/20" />
        <div className="absolute top-0 right-0 w-16 h-16 border-r-2 border-t-2 border-cyber-glow/20" />
        <div className="absolute bottom-0 left-0 w-16 h-16 border-l-2 border-b-2 border-cyber-glow/20" />
        <div className="absolute bottom-0 right-0 w-16 h-16 border-r-2 border-b-2 border-cyber-glow/20" />
      </div>

      {/* Keyboard hints - hidden on mobile */}
      <div className="hidden sm:block fixed bottom-6 right-6 z-40 pointer-events-none">
        <div className="flex gap-4 font-mono text-[10px] text-white/25">
          <span className="flex items-center gap-1.5">
            <kbd className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10">⌘K</kbd>
            Search
          </span>
          <span className="flex items-center gap-1.5">
            <kbd className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10">Esc</kbd>
            Back
          </span>
        </div>
      </div>
      
      {/* Credit */}
      <div className="fixed bottom-3 sm:bottom-6 left-3 sm:left-6 z-40 pointer-events-auto">
        <a 
          href="https://x.com/0xprismis" 
          target="_blank" 
          rel="noopener noreferrer"
          className="font-mono text-[10px] text-white/20 hover:text-white/50 transition-colors"
        >
          made by @0xprismis
        </a>
      </div>
      
      {/* Vercel Web Analytics */}
      <Analytics />
    </div>
  );
}

// Memoize AppContent to prevent unnecessary re-renders
const MemoizedAppContent = memo(AppContent);

// Export with global error boundary
export default function App() {
  return (
    <ErrorBoundary>
      <MemoizedAppContent />
    </ErrorBoundary>
  );
}
